import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { api, errMsg, money, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/admin/ui";
import { ClientFees } from "@/components/admin/ClientFees";

function FeeForm({ initial, onSave, onCancel, testId }: { initial?: any; onSave: (d: string, a: number) => Promise<void>; onCancel?: () => void; testId: string }) {
  const [d, setD] = useState(initial?.description || "");
  const [a, setA] = useState(initial ? String(initial.amount) : "");
  const [busy, setBusy] = useState(false);
  const amt = parseFloat(a);
  const ok = d.trim() && amt > 0;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    try { await onSave(d.trim(), Math.round(amt * 100) / 100); if (!initial) { setD(""); setA(""); } } finally { setBusy(false); }
  };
  return (
    <form className="flex flex-wrap gap-2" onSubmit={submit} data-testid={`${testId}-form`}>
      <input className="field-input h-11 min-w-[12rem] flex-1 text-sm" placeholder="e.g. Foreign background search – Canada" value={d} onChange={(e) => setD(e.target.value)} maxLength={200} data-testid={`${testId}-description`} />
      <div className="relative w-32">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]">$</span>
        <input className="field-input h-11 w-full pl-6 text-sm" type="number" min="0.01" step="0.01" placeholder="0.00" value={a} onChange={(e) => setA(e.target.value)} data-testid={`${testId}-amount`} />
      </div>
      <button className="btn-primary btn-sm h-11" disabled={!ok || busy} data-testid={`${testId}-save`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : initial ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {initial ? "Save" : "Add fee"}
      </button>
      {onCancel && <button type="button" className="btn-secondary btn-sm h-11" onClick={onCancel} data-testid={`${testId}-cancel`}><X className="h-4 w-4" /></button>}
    </form>
  );
}

export function ExtraFees({ c, onChanged }: { c: any; onChanged: () => void }) {
  const [editing, setEditing] = useState("");
  const fees = c.extra_fees || [];
  const total = fees.reduce((s: number, f: any) => s + Number(f.amount), 0);
  const run = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); toast.success(msg); setEditing(""); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = (f: any) => {
    if (!window.confirm(`Remove "${f.description}" (${money(f.amount)}) from this applicant's bill?`)) return;
    run(() => api.delete(`/admin/cases/${c.id}/extra-fees/${f.id}`), "Fee removed");
  };
  return (
    <Card title="Additional search fees" testId="extra-fees-card">
      <div className="space-y-4 p-5">
        <p className="text-xs text-[#64748B]">Fees added here are listed on the organization's monthly bill, each on its own line, for the month this case is completed. Cases that are still pending go on the bill for the month they're completed.</p>
        <ClientFees caseId={c.id} onChanged={onChanged} />
        <FeeForm testId="extra-fee-new" onSave={(d, a) => run(() => api.post(`/admin/cases/${c.id}/extra-fees`, { description: d, amount: a }), "Fee added to this applicant's bill")} />
        {fees.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-xl border border-[#E2E8F0]" data-testid="extra-fees-list">
            {fees.map((f: any) => (
              <li key={f.id} className="px-4 py-3" data-testid={`extra-fee-${f.id}`}>
                {editing === f.id ? (
                  <FeeForm testId={`extra-fee-edit-${f.id}`} initial={f} onCancel={() => setEditing("")} onSave={(d, a) => run(() => api.patch(`/admin/cases/${c.id}/extra-fees/${f.id}`, { description: d, amount: a }), "Fee updated")} />
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#1A2536]">{f.description}</p>
                      <p className="text-xs text-[#94A3B8]">{f.edited_by ? `Edited by ${f.edited_by} · ${fmtDateTime(f.edited_at)}` : `${f.by} · ${fmtDateTime(f.at)}`}</p>
                    </div>
                    <span className="font-mono text-sm font-semibold" data-testid={`extra-fee-amount-${f.id}`}>{money(f.amount)}</span>
                    <button className="btn-secondary btn-sm" title="Edit fee" aria-label="Edit fee" onClick={() => setEditing(f.id)} data-testid={`extra-fee-edit-btn-${f.id}`}><Pencil className="h-4 w-4" /></button>
                    <button className="btn-secondary btn-sm text-[#B91C1C]" title="Remove fee" aria-label="Remove fee" onClick={() => remove(f)} data-testid={`extra-fee-remove-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </li>
            ))}
            <li className="flex justify-between bg-[#F8FAFC] px-4 py-2.5 text-sm font-semibold"><span>Additional fees total</span><span className="font-mono" data-testid="extra-fees-total">{money(total)}</span></li>
          </ul>
        )}
      </div>
    </Card>
  );
}
